import { useCallback } from "react"
import { Keyboard, Platform, TextStyle, View, ViewStyle } from "react-native"

import { useFocusEffect } from "expo-router"
import * as ScreenOrientation from "expo-screen-orientation"
import { StatusBar } from "expo-status-bar"
import { useSafeAreaInsets } from "react-native-safe-area-context"

import { CloseButton } from "src/components/CloseButton"
import { Text } from "src/components/Ignite"
import { MemberFactsContent } from "src/components/MemberFacts"
import { SidekickView } from "src/components/SidekickView"
import { useAppTheme } from "src/hooks/useAppTheme"
import { ThemedStyle } from "src/theme"
import { backOrToRoot } from "src/utils/routerHelpers"

/**
 * MemberProfileScreen - A full-screen profile editor that uses SidekickView for consistent UX.
 *
 * Features:
 * - Avatar overlay with speech integration
 * - Talk/Type/Menu button cluster
 * - Profile editing with MemberFacts
 */
export default function MemberProfileScreen() {
  const insets = useSafeAreaInsets()
  const { themed, navTheme } = useAppTheme()

  // Lock to portrait orientation when this screen gains focus
  useFocusEffect(
    useCallback(() => {
      ScreenOrientation.lockAsync(ScreenOrientation.OrientationLock.PORTRAIT_UP).catch(() => {})
    }, []),
  )

  const handleClose = useCallback(() => {
    Keyboard.dismiss()
    // Small delay to allow keyboard to fully dismiss before navigating back
    // This prevents keyboard flickering when the chat screen's ChatTextInput auto-focuses
    setTimeout(() => {
      backOrToRoot()
    }, 100)
  }, [])

  return (
    <View style={themed($container)}>
      <StatusBar style={navTheme.dark ? "light" : "dark"} />

      <SidekickView
        showAvatar
        autoShowAvatarOnSpeech
        showCaptions
        showCCToggle
        showButtonCluster
        compactButtonCluster
        darkBackground={true}
        style={themed($container)}
        onMenuPress={handleClose}
        riveOwner="video"
        controlsConfig={{ type: "none" }}
      >
        {/* Custom Header - positioned above the content */}
        <View
          style={[
            themed($headerContainer),
            {
              top: insets.top,
              left: insets.left,
              right: insets.right,
            },
          ]}
          pointerEvents="box-none"
        >
          <Text style={themed($headerTitle)}>Your Profile</Text>
          <CloseButton
            onPress={handleClose}
            accessibilityLabel="Close profile"
            style={{ position: "absolute", top: 22, right: 16 }}
          />
        </View>
        {/* Profile Content */}
        <View
          style={[
            themed($contentWrapper),
            {
              paddingTop: 60, // Space for header (SidekickView provides insets.top)
            },
          ]}
        >
          <MemberFactsContent />
        </View>
      </SidekickView>
    </View>
  )
}

const $container: ThemedStyle<ViewStyle> = ({ colors }) => ({
  flex: 1,
  backgroundColor: colors.background,
})

const $headerContainer: ThemedStyle<ViewStyle> = () => ({
  position: Platform.OS === "web" ? ("fixed" as any) : "absolute",
  zIndex: 2100,
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  paddingHorizontal: 16,
  height: 80,
  // No backgroundColor — see menu.tsx for rationale (avoid occluding the
  // tab-mode avatar that lives in MediaPlayerShell's foreground layer).
})

const $headerTitle: ThemedStyle<TextStyle> = ({ colors }) => ({
  fontSize: 18,
  fontWeight: "700",
  color: colors.text,
})

const $contentWrapper: ThemedStyle<ViewStyle> = () => ({
  flex: 1,
})
